<script setup>
import { ref } from 'vue';
import { Head, usePage } from '@inertiajs/vue3';
import { useStorage } from '@vueuse/core';
import { FilterMatchMode } from '@primevue/core/api';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import AuthenticatedAdminLayout from '@/Layouts/AuthenticatedAdminLayout.vue';

const page = usePage();

const toast = useToast();

let minPickupDate = null;
let maxPickupDate = null;

const orders = ref(
    [ ...page.props.orders ].map(
        order => {
          const dobDate = order.dob ? new Date(order.dob) : null;
          const orderDate = order.order_date ? new Date(order.order_date) : null;
          const pickupDate = order.pickup_date ? new Date(order.pickup_date) : null;

          if (pickupDate) {
            if (!minPickupDate || (pickupDate < minPickupDate)) {
              if (!minPickupDate) {
                minPickupDate = new Date();
              }

              minPickupDate.setTime(pickupDate.getTime());
            }

            if (!maxPickupDate || (pickupDate > maxPickupDate)) {
              if (!maxPickupDate) {
                maxPickupDate = new Date();
              }

              maxPickupDate.setTime(pickupDate.getTime());
            }
          }

          return {
            ...order,
            dob: dobDate,
            order_date: orderDate,
            pickup_date: pickupDate,
          };
        }
    )
);

const DEFAULT_FILTERS = {
  reference: { value: null, matchMode: FilterMatchMode.CONTAINS },
  order_date: { value: null, matchMode: FilterMatchMode.DATE_IS },
  email: { value: null, matchMode: FilterMatchMode.CONTAINS },
  name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  dob: { value: null, matchMode: FilterMatchMode.DATE_IS },
  quantity: { value: null, matchMode: FilterMatchMode.EQUALS },
  total_amount: { value: null, matchMode: FilterMatchMode.EQUALS },
  status: { value: null, matchMode: FilterMatchMode.EQUALS },
  pickup_date: { value: null, matchMode: FilterMatchMode.DATE_IS },
  pickup_hour: { value: null, matchMode: FilterMatchMode.EQUALS },
};

// The data table component does not have an equivalent for "mergeDefaults", and crashes when new filters are added.
const filters = useStorage(
    'order-dashboard-filters',
    DEFAULT_FILTERS,
    sessionStorage,
    { mergeDefaults: true }
)

const resetFilters = () => {
  filters.value = { ...DEFAULT_FILTERS };
};

const STATUS_LABELS = {
  pending: 'En attente de paiement',
  canceled: 'Annulée',
  processing: 'Payée',
  completed: 'Terminée',
  pending_refund: 'À rembourser',
  refunded: 'Remboursée',
};

const STATUS_SEVERITIES = {
  canceled: 'danger',
  completed: 'secondary',
  pending: 'info',
  pending_refund: 'warn',
  processing: 'primary',
  refunded: 'contrast',
};

const STATUS_UPDATE_SUCCESS_MESSAGE = 'Le statut de la commande a bien été mis à jour.';

const ACTION_VALIDATE_PAYMENT = {
  label: 'Valider le paiement',
  icon: 'pi pi-euro',
  severity: 'primary',
  successMessage: STATUS_UPDATE_SUCCESS_MESSAGE,
  routeName: 'admin.order.validate-payment',
};

const ACTION_CANCEL_ORDER = {
  label: 'Annuler la commande',
  icon: 'pi pi-times',
  severity: 'danger',
  successMessage: STATUS_UPDATE_SUCCESS_MESSAGE,
  routeName: 'admin.order.cancel',
};

const ACTION_COMPLETE_ORDER = {
  label: 'Traiter la commande',
  icon: 'pi pi-check',
  severity: 'secondary',
  successMessage: STATUS_UPDATE_SUCCESS_MESSAGE,
  routeName: 'admin.order.complete',
};

const ACTION_MARK_ORDER_TO_REFUND = {
  label: 'Marquer la commande comme "à rembourser"',
  icon: 'pi pi-tag',
  severity: 'warn',
  successMessage: STATUS_UPDATE_SUCCESS_MESSAGE,
  routeName: 'admin.order.mark-to-refund',
};

const ACTION_REFUND_ORDER = {
  label: 'Rembourser la commande',
  icon: 'pi pi-undo',
  severity: 'contrast',
  successMessage: STATUS_UPDATE_SUCCESS_MESSAGE,
  routeName: 'admin.order.refund',
};

const ACTION_RESEND_ORDER_CONFIRMATION_MAIL = {
  label: 'Renvoyer le mail de confirmation',
  icon: 'pi pi-send',
  severity: 'info',
  successMessage: 'Le mail de confirmation a bien été renvoyé.',
  routeName: 'admin.order.resend-confirmation-mail',
};

const ACTION_PRINT_ORDER = {
  label: 'Imprimer la commande',
  icon: 'pi pi-print',
  severity: 'secondary',
  isUsingNewTab: true,
  routeName: 'order.pickup-slip',
  getOrderRouteParams: (order) => ({ key: order.pickup_slip_key }),
};

const getStatusActions = status => {
  switch (status) {
    case 'pending':
      return [
        ACTION_VALIDATE_PAYMENT,
        ACTION_CANCEL_ORDER,
      ];
    case 'processing':
      return [
        ACTION_COMPLETE_ORDER,
        ACTION_MARK_ORDER_TO_REFUND,
        ACTION_REFUND_ORDER,
        ACTION_PRINT_ORDER,
        ACTION_RESEND_ORDER_CONFIRMATION_MAIL,
      ];
    case 'pending_refund':
      return [
        ACTION_REFUND_ORDER,
      ];
    case 'completed':
      return [
        ACTION_MARK_ORDER_TO_REFUND,
        ACTION_REFUND_ORDER,
        ACTION_RESEND_ORDER_CONFIRMATION_MAIL,
      ];
    default:
      return [];
  }
};


const filteredOrders = ref(orders.value);
const visibleOrders = ref([]);
const allSelectedOrders = ref([]);
const filteredSelectedOrders = ref([]);
const firstRowIndex = ref(0);
const visibleRowCount = ref(20);
const isAllSelectedOnPage = ref(false);

const onOrderListChange = (list) => {
  // Important note: do not copy, keep the same reference, so that status updates can be picked up by the UI.
  filteredOrders.value = list;
  filteredSelectedOrders.value = filteredOrders.value.filter(order => allSelectedOrders.value.includes(order));

  const newVisibleOrders = list.slice(
      firstRowIndex.value,
      firstRowIndex.value + visibleRowCount.value
  );

  visibleOrders.value = newVisibleOrders;
  isAllSelectedOnPage.value = newVisibleOrders.every(order => allSelectedOrders.value.includes(order));
}

const addOrdersToSelection = (orders) => {
  const newAllSelectedOrders = [ ...allSelectedOrders.value ];
  const newFilteredSelectedOrders = [];

  for (const order of orders) {
    if (!newAllSelectedOrders.includes(order)) {
      newAllSelectedOrders.push(order);
    }

    if (filteredOrders.value.includes(order)) {
      newFilteredSelectedOrders.push(order);
    }
  }

  allSelectedOrders.value = newAllSelectedOrders;
  filteredSelectedOrders.value = newFilteredSelectedOrders;
};

const removeOrdersFromSelection = (orders) => {
  allSelectedOrders.value = allSelectedOrders.value.filter(order => !orders.includes(order));
  filteredSelectedOrders.value = filteredSelectedOrders.value.filter(order => !orders.includes(order));
};

const onAllSelectedChange = (event) => {
  isAllSelectedOnPage.value = event.checked;

  if (isAllSelectedOnPage.value) {
    addOrdersToSelection(visibleOrders.value);
  } else {
    removeOrdersFromSelection(visibleOrders.value);
  }
}

const onOrderSelect = (event) => {
  isAllSelectedOnPage.value = visibleOrders.value.every(order => allSelectedOrders.value.includes(order));
  filteredSelectedOrders.value = [ ...filteredSelectedOrders.value, event.data ];
}

const onOrderUnselect = (event) => {
  isAllSelectedOnPage.value = false;
  filteredSelectedOrders.value = filteredSelectedOrders.value.filter(({ id }) => id !== event.data.id);
}

const selectFilteredOrders = () => {
  isAllSelectedOnPage.value = true;
  addOrdersToSelection(filteredOrders.value);
};

const unselectFilteredOrders = () => {
  isAllSelectedOnPage.value = false;
  removeOrdersFromSelection(filteredOrders.value);
};

const selectAllOrders = () => {
  isAllSelectedOnPage.value = true;
  allSelectedOrders.value = [ ...orders.value ];
  filteredSelectedOrders.value = [ ...filteredOrders.value ];
};

const unselectAllOrders = () => {
  isAllSelectedOnPage.value = false;
  allSelectedOrders.value = [];
  filteredSelectedOrders.value = [];
};

const submittedOrderIds = ref(new Set());

const applyActionToOrder = (action, order) => {
  const routeParams = (action.getOrderRouteParams || (({ id }) => id))(order);

  if (action.isUsingNewTab) {
    window.open(route(action.routeName, routeParams), '_blank');
  } else if (
      !submittedOrderIds.value.has(order.id)
      && confirm(`${action.label} - Êtes-vous sûr(e) ?`)
  ) {
    submittedOrderIds.value.add(order.id);

    axios.post(route(action.routeName, routeParams))
         .then((response) => {
           if (response.data.success) {
             toast.add({
               severity: 'success',
               summary: 'Commande traitée',
               detail: action.successMessage,
               life: 5000,
             });

             if (response.data.new_status) {
               for (const [ index, listOrder ] of orders.value.entries()) {
                 if (order.id === listOrder.id) {
                   orders.value[index].status = response.data.new_status;
                 }
               }

               for (const [ index, listOrder ] of filteredOrders.value.entries()) {
                 if (order.id === listOrder.id) {
                   filteredOrders.value[index].status = response.data.new_status;
                 }
               }
             }
           } else {
             throw response;
           }
         })
         .catch((error) => {
           toast.add({
             severity: 'error',
             summary: 'Erreur de traitement',
             detail: (
                 error.response?.data?.error
                 || error.response?.error
                 || 'Une erreur est survenue lors du traitement de la commande.'
             ),
             life: 5000,
           });
         })
         .finally(() => {
           submittedOrderIds.value.delete(order.id);
         });
  }
};

const applyExportActionToSelectedOrders = (routeName) => {
  const orderIds = allSelectedOrders.value.map(order => order.id);

  if (orderIds.length === 0) {
    toast.add({ severity: 'warn', summary: 'Aucune commande sélectionnée', life: 5000 });

    return;
  }

  const form = document.createElement('form');
  form.action = route(routeName);
  form.method = 'POST';
  form.target = '_blank';
  form.style.display = 'none';

  const csrfInput = document.createElement('input');
  csrfInput.type = 'hidden';
  csrfInput.name = '_token';
  csrfInput.value = page.props.csrf_token;
  form.appendChild(csrfInput);

  const orderIdsInput = document.createElement('input');
  orderIdsInput.type = 'hidden';
  orderIdsInput.name = 'ids';
  orderIdsInput.value = orderIds.join(',');
  form.appendChild(orderIdsInput);

  document.body.appendChild(form);

  form.submit();
};

const exportSelectedEnvelopes = () => applyExportActionToSelectedOrders('admin.order.envelope.export');

const printSelectedPickupSlips = () => applyExportActionToSelectedOrders('admin.order.pickup-slip.mass-print');

const currencyFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
</script>

<template>
  <AuthenticatedAdminLayout>
    <Head title="Gestion des commandes"/>

    <main class="relative p-4 sm:px-6 lg:px-8 flex flex-col justify-center sm:items-center mx-auto size-full">
      <div class="flex flex-row justify-between items-center pb-4 size-full">
        <h1 class="text-xl sm:text-2xl text-primary uppercase font-semibold">
          Gestion des commandes
        </h1>
      </div>

      <div class="flex flex-row justify-between items-start size-full">
        <div class="pb-4 flex flex-col gap-2">
          <div class="flex flex-row flex-wrap items-center gap-4">
            <div class="text-sm text-slate-500 font-semibold">
              Sélectionner
            </div>

            <Button
                :label="`Résultats (${filteredOrders.length})`"
                severity="secondary"
                variant="outlined"
                @click="selectFilteredOrders"
            />

            <Button
                :label="`Tout (${orders.length})`"
                severity="secondary"
                variant="outlined"
                @click="selectAllOrders"
            />

            <div class="text-sm text-slate-500 font-semibold">
              Désélectionner
            </div>

            <Button
                :label="`Résultats (${filteredSelectedOrders.length})`"
                severity="secondary"
                variant="outlined"
                @click="unselectFilteredOrders"
            />

            <Button
                :label="`Tout (${allSelectedOrders.length})`"
                severity="secondary"
                variant="outlined"
                @click="unselectAllOrders"
            />
          </div>

          <div class="pb-2 text-sm italic text-slate-500 size-full">
            Résultats :

            <span class="font-bold">{{ filteredOrders.length }}</span>
            {{ (filteredOrders.length > 1) ? 'commandes' : 'commande' }} sur
            <span class="font-bold">{{ orders.length }}</span>

            / Sélection :

            <span class="font-bold">{{ allSelectedOrders.length }}</span>
            {{ (allSelectedOrders.length > 1) ? 'commandes' : 'commande' }} sur
            <span class="font-bold">{{ orders.length }}</span>
          </div>
        </div>

        <div class="pb-4 flex flex-row flex-wrap gap-4">
          <Button
              label="Exporter les enveloppes"
              icon="pi pi-envelope"
              severity="primary"
              @click="exportSelectedEnvelopes"
              :disabled="allSelectedOrders.length === 0"
          />

          <Button
              label="Imprimer les bons de retrait"
              icon="pi pi-print"
              severity="primary"
              @click="printSelectedPickupSlips"
              :disabled="allSelectedOrders.length === 0"
          />

          <Button
              label="Réinitialiser les filtres"
              icon="pi pi-filter-slash"
              severity="secondary"
              @click="resetFilters"
          />
        </div>
      </div>

      <DataTable
          :value="orders"
          dataKey="id"
          v-model:filters="filters"
          v-model:first="firstRowIndex"
          v-model:rows="visibleRowCount"
          v-model:selection="allSelectedOrders"
          filterDisplay="row"
          resizableColumns
          columnResizeMode="expand"
          paginator
          :pageLinkSize="10"
          :rows="20"
          :rowsPerPageOptions="[10, 20, 50, 100]"
          removableSort
          scrollable
          scrollHeight="calc(100vh - 19.5rem)"
          size="large"
          showGridlines
          stripedRows
          style="width: 100%;"
          :selectAll="isAllSelectedOnPage"
          @select-all-change="onAllSelectedChange"
          @row-select="onOrderSelect"
          @row-unselect="onOrderUnselect"
          @value-change="onOrderListChange"
      >
        <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>

        <Column field="reference" header="N°" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="order_date" dataType="date" header="Date commande" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
                id="order_date_filter"
                fluid
                v-model="filterModel.value"
                dateFormat="dd/mm/yy"
                @update:modelValue="filterCallback"
            />
          </template>

          <template #body="{ data }">
            <span v-if="data.order_date">
              {{ data.order_date.toLocaleDateString() }}
              {{ data.order_date.toLocaleTimeString() }}
            </span>
          </template>
        </Column>

        <Column field="email" header="E-mail" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="name" header="Nom" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="dob" dataType="date" header="Date de naissance" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
                id="dob_filter"
                fluid
                v-model="filterModel.value"
                dateFormat="dd/mm/yy"
                @update:modelValue="filterCallback"
            />
          </template>

          <template #body="{ data }">
            <span v-if="data.dob">
              {{ data.dob.toLocaleDateString() }}
            </span>
          </template>
        </Column>

        <Column field="quantity" dataType="numeric" header="Qté" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="total_amount" dataType="numeric" header="Total" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>

          <template #body="{ data }">
            <span v-if="data.total_amount">
              {{ currencyFormatter.format(data.total_amount) }}
            </span>
          </template>
        </Column>

        <Column field="status" header="Statut" :showFilterMenu="false" :showClearButton="false" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <Select
                v-model="filterModel.value"
                :options="Object.keys(STATUS_LABELS)"
                :optionLabel="option => STATUS_LABELS[option]"
                placeholder="Statut"
                @change="filterCallback()"
                :showClear="true"
            >
              <template #option="{ option }">
                <Tag :value="STATUS_LABELS[option]" :severity="STATUS_SEVERITIES[option]"/>
              </template>
            </Select>
          </template>

          <template #body="{ data }">
            <Tag :value="STATUS_LABELS[data.status]" :severity="STATUS_SEVERITIES[data.status]"/>
          </template>
        </Column>

        <Column field="pickup_date" dataType="date" header="Date retrait" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
                id="pickup_date_filter"
                fluid
                v-model="filterModel.value"
                dateFormat="dd/mm/yy"
                :minDate="minPickupDate"
                :maxDate="maxPickupDate"
                @update:modelValue="filterCallback"
            />
          </template>

          <template #body="{ data }">
            <span v-if="data.pickup_date">
              {{ data.pickup_date.toLocaleDateString() }}
            </span>
          </template>
        </Column>

        <Column field="pickup_hour" dataType="numeric" header="Heure retrait" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column
            field="actions"
            header="Actions"
            frozen
            alignFrozen="right"
            style="width: 5em !important; max-width: none !important;"
        >
          <template #body="{ data, index }">
            <div class="flex justify-start gap-3">
              <Button
                  v-for="action in getStatusActions(data.status)"
                  :key="action.routeName"
                  :severity="action.severity"
                  size="small"
                  rounded
                  @click="() => applyActionToOrder(action, data)"
                  :disabled="submittedOrderIds.has(data.id)"
                  :title="action.label"
              >
                <i :class="`pi ${action.icon}`"/>
              </Button>
            </div>
          </template>
        </Column>

        <template #empty>
          <div class="my-4 font-bold text-center">Aucune commande trouvée.</div>
        </template>
      </DataTable>

      <Toast/>
    </main>
  </AuthenticatedAdminLayout>
</template>
